import { useModalContext } from 'utils/contexts/modal';
import { Delete } from 'ui/icons/action/Delete';
import { GhostButton } from 'ui/button';
import { LeftTooltip, Tooltip } from 'ui/tooltip/Tooltip';
import { useDisplayDateTz } from 'utils/hooks/datetime';
import { DATE_FORMAT_WITH_COMMA_HOUR_MINS } from 'utils/date';
import { useTranslations } from './useTranslations';
import { MovableInkIntegrationColumn } from '../types';
import {
  StyledTableCell,
  SecondTier,
  DisplayWrapper,
  CreatedByWrapper,
} from '../MovableInkIntegrations.styles';

import type { MovableInkIntegrationTableColumnData, MovableInkModalState } from '../types';
import { MovableInkModal } from '../constants';
import { Avatar, AVATAR_SIZES } from 'ui/avatar';

export const useTableColumns = (): MovableInkIntegrationTableColumnData[] => {
  const { showModal } = useModalContext<MovableInkModalState>();
  const { t } = useTranslations();
  const displayDateTz = useDisplayDateTz();

  return [
    {
      name: t(`table.columns.${MovableInkIntegrationColumn.name}.label`),
      align: 'flex-start',
      value: MovableInkIntegrationColumn.name,
      sortable: true,
      RowCell: ({ row: { name, tokenId } }) => (
        <StyledTableCell data-first-column={true}>
          <DisplayWrapper>{name}</DisplayWrapper>
          <SecondTier>
            {t(`table.columns.${MovableInkIntegrationColumn.name}.tokenId`, { tokenId })}
          </SecondTier>
        </StyledTableCell>
      ),
    },
    {
      name: t(`table.columns.${MovableInkIntegrationColumn.accountId}.label`),
      align: 'flex-start',
      value: MovableInkIntegrationColumn.accountId,
      sortable: true,
      RowCell: ({ row: { accountId } }) => (
        <StyledTableCell>
          <DisplayWrapper>{accountId}</DisplayWrapper>
        </StyledTableCell>
      ),
    },
    {
      name: t(`table.columns.${MovableInkIntegrationColumn.createdBy}.label`),
      align: 'flex-start',
      value: MovableInkIntegrationColumn.createdBy,
      sortable: true,
      RowCell: ({ row: { createdBy, createdByUser } }) => {
        const [firstName, lastName] = createdByUser?.fullName.split(' ') || [];
        return (
          <StyledTableCell>
            <CreatedByWrapper>
              <Tooltip isEnabled={!!createdBy} tooltipContent={createdBy}>
                <Avatar firstName={firstName} lastName={lastName} size={AVATAR_SIZES.MEDIUM} />
              </Tooltip>
              <div>
                {firstName} {lastName}
              </div>
            </CreatedByWrapper>
          </StyledTableCell>
        );
      },
    },
    {
      name: t(`table.columns.${MovableInkIntegrationColumn.createdAt}.label`),
      align: 'flex-start',
      value: MovableInkIntegrationColumn.createdAt,
      sortable: true,
      RowCell: ({ row: { createdAt } }) => (
        <StyledTableCell>
          {displayDateTz(new Date(createdAt), DATE_FORMAT_WITH_COMMA_HOUR_MINS)}
        </StyledTableCell>
      ),
    },
    {
      name: t(`table.columns.${MovableInkIntegrationColumn.updatedAt}.label`),
      align: 'flex-start',
      value: MovableInkIntegrationColumn.updatedAt,
      sortable: true,
      RowCell: ({ row: { updatedAt } }) => (
        <StyledTableCell>
          {displayDateTz(new Date(updatedAt), DATE_FORMAT_WITH_COMMA_HOUR_MINS)}
        </StyledTableCell>
      ),
    },
    {
      name: '',
      align: 'flex-end',
      value: '',
      sortable: false,
      RowCell: ({ row: { tokenId, name } }) => (
        <StyledTableCell>
          <LeftTooltip tooltipContent={t('table.columns.delete.tooltip', { tokenId })}>
            <GhostButton
              data-test={`deleteMIIntegration-${tokenId}`}
              onClick={() => {
                showModal({ modalId: MovableInkModal.Delete, id: tokenId, name });
              }}
            >
              <Delete height={20} />
              {t('table.columns.delete.label')}
            </GhostButton>
          </LeftTooltip>
        </StyledTableCell>
      ),
    },
  ];
};
